import React, { type ReactNode } from 'react'
import classNames from 'classnames'
import { FormattedMessage, type IntlShape, useIntl } from 'react-intl'

import { SliteLogo } from '@slite/components'
import { CheckmarkIcon, CloseIcon } from '@slite/icons'

import { Button } from '../../components/Button/Button'
import { Section } from '../../components/Section'
import { WebsiteText } from '../../components/WebsiteText'
import { useRoutes } from '../../routes'
import { getIconFromCompetitor } from './constants'

const getSliteFeatures = (
  intl: IntlShape
): { id: CheckedFeatures; title: string }[] => {
  return [
    {
      id: 'company-wiki-templates',
      title: intl.formatMessage({
        defaultMessage: 'Company wiki & templates',
        id: 'website.compare.sectionTable.feature1.title',
      }),
    },
    {
      id: 'mcp-integration',
      title: intl.formatMessage({
        defaultMessage: 'MCP integration',
        id: 'website.compare.sectionTable.feature.mcp.title',
      }),
    },
    {
      id: 'enterprise-search',
      title: intl.formatMessage({
        defaultMessage: 'Enterprise search across tools',
        id: 'website.compare.sectionTable.feature.enterpriseSearch.title',
      }),
    },
    {
      id: 'ai-on-every-plan',
      title: intl.formatMessage({
        defaultMessage: 'AI answers on every paid plan',
        id: 'website.compare.sectionTable.feature.aiPricing.title',
      }),
    },
    {
      id: 'ai-workflows',
      title: intl.formatMessage({
        defaultMessage: 'AI workflows & automations',
        id: 'website.compare.sectionTable.feature.aiWorkflows.title',
      }),
    },
    {
      id: 'document-verification',
      title: intl.formatMessage({
        defaultMessage: 'Document verification',
        id: 'website.compare.sectionTable.feature2.title',
      }),
    },
    {
      id: 'knowledge-management-panel',
      title: intl.formatMessage({
        defaultMessage: 'Knowledge management panel',
        id: 'website.compare.sectionTable.feature3.title',
      }),
    },
    {
      id: 'similar-and-duplicate-docs-detection',
      title: intl.formatMessage({
        defaultMessage: 'Similar & duplicate docs detection',
        id: 'website.compare.sectionTable.feature6.title',
      }),
    },
    {
      id: 'inactive-docs-detection',
      title: intl.formatMessage({
        defaultMessage: 'Inactive docs detection',
        id: 'website.compare.sectionTable.feature7.title',
      }),
    },
    {
      id: 'fast-and-accurate-search',
      title: intl.formatMessage({
        defaultMessage: 'Fast and accurate search',
        id: 'website.compare.sectionTable.feature5.title',
      }),
    },
    {
      id: 'easy-to-master-and-fast-to-deploy',
      title: intl.formatMessage({
        defaultMessage: 'Easy to master and fast to deploy',
        id: 'website.compare.sectionTable.feature8.title',
      }),
    },
  ]
}

type CheckedFeatures =
  | 'company-wiki-templates'
  | 'mcp-integration'
  | 'enterprise-search'
  | 'ai-on-every-plan'
  | 'ai-workflows'
  | 'document-verification'
  | 'knowledge-management-panel'
  | 'similar-and-duplicate-docs-detection'
  | 'inactive-docs-detection'
  | 'fast-and-accurate-search'
  | 'easy-to-master-and-fast-to-deploy'
export const SectionTable = ({
  title,
  description,
  checkedFeatures,
  hiddenFeatures = [],
  competitorName,
  background = 'shade-00',
}: {
  title: string
  description: string | ReactNode
  checkedFeatures: CheckedFeatures[]
  hiddenFeatures?: CheckedFeatures[]
  competitorName: string
  background?: string
}) => {
  const routes = useRoutes()
  const intl = useIntl()

  const sliteFeatures = getSliteFeatures(intl).filter(
    (f) => !hiddenFeatures.includes(f.id)
  )
  const CompetitorIcon = getIconFromCompetitor(competitorName)

  const sortedFeatures = [...sliteFeatures].sort((a, b) => {
    const aIsChecked = checkedFeatures.includes(a.id)
    const bIsChecked = checkedFeatures.includes(b.id)

    if (aIsChecked && !bIsChecked) return -1
    if (!aIsChecked && bIsChecked) return 1
    return 0
  })

  return (
    <Section
      background={background}
      containerTopPadding={40}
      containerBottomPadding={40}
    >
      <div className="grid-12 grid-gap-60 grid-align-center">
        <div
          className={classNames(
            'grid-span-5 grid-span-12-small grid-span-10-medium',
            'shade-100 flexbox flexbox-column flexbox-align-start'
          )}
        >
          <WebsiteText variant="smallTitle" className="margin-bottom-16">
            {title}
          </WebsiteText>
          <WebsiteText variant="body-lg" className="margin-bottom-24">
            {description}
          </WebsiteText>
          <Button href={routes.bookDemo()} variant="primary" size="huge">
            <FormattedMessage
              defaultMessage="Good for me"
              id="website.compare.sectionTable.button"
            />
          </Button>
        </div>
        <div
          className={classNames(
            'grid-span-7 grid-span-12-medium grid-span-12-small',
            'radius-larger border-small shade-100 padding-vertical-40 padding-horizontal-40  padding-horizontal-24-small padding-vertical-32-small'
          )}
          style={{ background: '#ffffff1a', borderColor: '#ffffff1a' }}
        >
          <div
            className="padding-bottom-16 border-bottom-small grid-12 grid-align-center grid-column-gap-24"
            style={{ borderColor: '#ffffff1a' }}
          >
            <div className="grid-span-6">
              <FormattedMessage
                defaultMessage="Features"
                id="website.compare.sectionTable.featuresTitle"
              />
            </div>
            <div className="grid-span-3 flexbox flexbox-align-center flexbox-column-gap-8 flexbox-justify-center padding-right-8">
              <SliteLogo
                variant="symbol-no-bg"
                width={20}
                height={20}
                className="shade-100 display-none-small"
              />
              {/*  eslint-disable-next-line formatjs/no-literal-string-in-jsx */}
              <div className="font-family-title font-size-5">Slite</div>
            </div>
            <div className="grid-span-3 flexbox flexbox-align-center flexbox-column-gap-8 flexbox-justify-center padding-right-">
              <CompetitorIcon
                width={20}
                height={20}
                className="shade-100 flexbox-no-shrink display-none-small"
              />

              <div className="font-family-title font-size-5">
                {competitorName}
              </div>
            </div>
          </div>
          {sortedFeatures.map((feature, index) => (
            <div
              key={feature.id}
              className={classNames(
                'font-size-5 padding-vertical-16 grid-12 grid-align-center grid-column-gap-24',
                index < sortedFeatures.length - 1 && 'border-bottom-small'
              )}
              style={{ borderColor: '#ffffff1a' }}
            >
              <div className="grid-span-6">{feature.title}</div>
              <div className="dark-mode grid-span-3 flexbox flexbox-justify-center padding-right-8">
                <div
                  className="slite-editor-database-tag-green radius-round flexbox flexbox-centered"
                  style={{ width: 24, height: 24 }}
                >
                  <CheckmarkIcon size={14} />
                </div>
              </div>
              <div className="dark-mode grid-span-3 flexbox flexbox-justify-center padding-right-">
                {checkedFeatures.includes(feature.id) ? (
                  <div
                    className="slite-editor-database-tag-green radius-round flexbox flexbox-centered"
                    style={{ width: 24, height: 24 }}
                  >
                    <CheckmarkIcon size={14} />
                  </div>
                ) : (
                  <div
                    className="slite-editor-database-tag-red radius-round flexbox flexbox-centered"
                    style={{ width: 24, height: 24 }}
                  >
                    <CloseIcon size={14} />
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    </Section>
  )
}
